import { useTranslation } from "@sagot/i18n";
import { Button, Icon, toast } from "@sagot/ui";
import { useErrorMessage } from "../../../lib/api-errors";
import { usePermissions } from "../../../session/permissions";
import type { AgentConfigOut } from "../shared/types";
import { STUDIO_TITLE_ID } from "./layout";
import { useAgentLifecycle } from "./use-agent-lifecycle";

export type AgentStatus = AgentConfigOut["status"];

const K = "deployKnowledge.widgetStudio.agentGate";

interface AgentNotLiveNoticeProps {
  agentId: string;
  /** Unknown (still loading / not readable) renders nothing; only `live` is also silent. */
  status: AgentStatus | undefined;
}

/** The agent's go-live state decides whether the widget loads: draft and paused agents 404 publicly. */
export function AgentNotLiveNotice({ agentId, status }: AgentNotLiveNoticeProps) {
  const { t } = useTranslation();
  const message = useErrorMessage();
  const permissions = usePermissions(agentId);
  const lifecycle = useAgentLifecycle(agentId);
  if (status !== "draft" && status !== "paused") return null;

  const canAct = !permissions.readOnly && permissions.can("agents.publish", { agentId });
  const action = status === "draft" ? lifecycle.publish : lifecycle.resume;
  const run = () => {
    action.mutate(undefined, {
      onSuccess: () => {
        toast.success(t(`${K}.${status}.done`));
        document.getElementById(STUDIO_TITLE_ID)?.focus();
      },
    });
  };

  return (
    <div role="status" data-testid="agent-not-live" className="flex items-start gap-3 rounded-lg border border-warning bg-warning-soft p-3.5 text-sm">
      <Icon name={status === "draft" ? "alert-triangle" : "pause"} className="mt-0.5 size-5 shrink-0 text-warning" />
      <div className="min-w-0 flex-1 space-y-2.5">
        <p className="font-semibold">{t(`${K}.title`)}</p>
        <p className="text-fg-muted">{t(`${K}.${status}.body`)}</p>
        {canAct ? (
          <Button size="sm" loading={action.isPending} onClick={run}>
            <Icon name={status === "draft" ? "upload-cloud" : "play"} />
            {t(`${K}.${status}.action`)}
          </Button>
        ) : (
          <p className="font-semibold">{t(`${K}.${status}.askAdmin`)}</p>
        )}
        {action.isError ? (
          <p role="alert" className="text-xs font-semibold text-danger">
            {message(action.error)}
          </p>
        ) : null}
      </div>
    </div>
  );
}
